import {
  LitElement,
  html,
  css,
  classMap,
  utils,
  unsafeCSS,
} from '@hh/global.js'
const { debounce } = utils

class HHPredictiveSearch extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    openerSelector: {
      type: String,
      attribute: 'opener-selector',
    },
    searchTerm: {
      type: String,
    },
    searchActive: {
      type: Boolean,
      attribute: 'active',
      reflect: true,
    },
    noResults: {
      type: Boolean,
      attribute: 'no-results',
      reflect: true,
    },
    cachedResults: {
      type: Object,
    },
    formStyle: {
      type: String,
      attribute: 'form-style',
    },
    loading: {
      type: Boolean,
    },
    isFocused: {
      type: Boolean,
    },
  }

  constructor() {
    super()

    this.debounceSearchInput = debounce(this.onSearchInput.bind(this), 500)

    this.cachedResults = {}

    this.classes = {
      hidden: 'hidden',
    }

    this.loading = false
    this.isFocused = false

    this.minChars = 2

    // https://shopify.dev/docs/api/ajax/reference/predictive-search
    this.resourceTypes = ['product', 'query']

    this.searchFields = [
      'body',
      'product_type',
      'tag',
      'title',
      'variants.barcode',
      'variants.sku',
      'variants.title',
      'vendor',
    ]

    this.featuredProductsCarousel = this.querySelector(
      '.js-featured-products-carousel'
    )

    document.addEventListener('search:close', () => {
      this.resetSearch()
    })
  }

  get opener() {
    return document.querySelector(this.openerSelector) ?? null
  }

  get form() {
    return (
      this.shadowRoot
        .querySelector('slot[name="search-form-header"]')
        ?.assignedNodes()[0] ?? null
    )
  }

  get searchInput() {
    return this.form?.querySelector('input[type="search"]') ?? null
  }

  get clearButton() {
    return this.form?.querySelector('.js-search-clear') ?? null
  }

  get searchResults() {
    return (
      this.shadowRoot
        .querySelector('slot[name="search-results"]')
        ?.assignedNodes()[0] ?? null
    )
  }

  get topSuggestionsSearches() {
    return (
      this.shadowRoot
        .querySelector('slot[name="search-inactive"]')
        ?.assignedNodes()[0] ?? null
    )
  }

  get quickSearchButtons() {
    return [...this.querySelectorAll('[data-search-term]')]
  }

  connectedCallback() {
    super.connectedCallback()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  firstUpdated() {
    this.searchInput.addEventListener('input', this.onSearchInput.bind(this))
    this.searchInput.value = this.searchTerm ?? ''
    this.clearButton.addEventListener('click', this.onClear.bind(this))
    this.searchInput?.addEventListener('focus', () => {
      this.isFocused = true
    })
    this.quickSearchButtons.forEach((button) => {
      button.addEventListener('click', this.onQuickSearch.bind(this))
    })
  }

  updated(changedProperties) {
    if (changedProperties.has('searchTerm')) {
      this.searchChange(this.searchTerm)
    }
  }

  onSearchInput(e) {
    if (!this.searchInput) return

    const { value } = e.target

    if (value.length === 0) {
      if (this.searchResults) this.searchResults.innerHTML = ''
      this.topSuggestionsSearches?.classList.remove(this.classes.hidden)
      this.refreshFocusTrap()
    }

    this.searchTerm = value
  }

  onClear() {
    this.searchInput.value = ''
    this.searchTerm = ''
    this.searchActive = false
    if (this.searchResults) this.searchResults.innerHTML = ''
    this.refreshFocusTrap()
    this.topSuggestionsSearches?.classList.remove(this.classes.hidden)
  }

  onQuickSearch(e) {
    this.isFocused = true
    e.preventDefault()
    const value = e.currentTarget.dataset.searchTerm

    if (!this.searchInput) return

    this.searchInput.value = value
    const inputEvent = new Event('input', { bubbles: true, composed: true })
    this.searchInput.dispatchEvent(inputEvent)
  }

  searchChange(query) {
    const newSearchTerm = query.trim()
    if (newSearchTerm.length < this.minChars) {
      this.searchActive = false
      this.clearButton.classList.add(this.classes.hidden)
      this.topSuggestionsSearches?.classList.remove(this.classes.hidden)
      return
    }

    this.searchActive = true
    this.getSearchResults(newSearchTerm)
    this.clearButton.classList.remove(this.classes.hidden)
    this.topSuggestionsSearches?.classList.add(this.classes.hidden)
  }

  async getSearchResults(query) {
    const queryKey = query.replaceAll(' ', '-').toLowerCase()

    if (this.cachedResults?.[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    this.loading = true
    const searchEndpoint = window.routes.predictive_search_url

    const params = {
      q: query,
      'resources[type]': this.resourceTypes.join(','),
      'resources[options][fields]': this.searchFields.join(','),
      section_id: 'predictive-search',
    }

    const paramsString = new URLSearchParams(params).toString()
    const searchUrl = `${searchEndpoint}?${paramsString}`

    try {
      const res = await fetch(searchUrl)
      if (!res.ok) throw new Error(res.status)

      const text = await res.text()
      const resultsMarkup = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search')

      this.cachedResults[queryKey] = resultsMarkup
      this.loading = false
      this.renderSearchResults(resultsMarkup)
    } catch (error) {
      this.loading = false
      console.error('An error occurred in the predictive search request: ', {
        error,
        query,
        searchUrl,
      })
    }
  }

  renderSearchResults(resultsMarkup) {
    this.noResults = resultsMarkup.querySelector('.js-no-results') !== null
    this.searchResults.innerHTML = resultsMarkup.innerHTML
    this.refreshFocusTrap()
  }

  refreshFocusTrap(timeout = 0) {
    setTimeout(() => {
      this.dispatchEvent(
        new CustomEvent('focus-trap:refresh', { bubbles: true, composed: true })
      )
    }, timeout)
  }

  resetSearch() {
    this.onClear()
    this.featuredProductsCarousel &&
      this.featuredProductsCarousel.scrollTo(0, 0)
    this.isFocused = false
  }

  render() {
    return html`
      <div
        class="predictive-search-wrapper ${classMap({
          pinned: this.isFocused || this.searchActive,
        })}"
      >
        <slot name="search-form-header"></slot>

        <div class="search-form-wrapper">
          <slot name="search-form"></slot>
        </div>

        <div
          class="search-container ${classMap({
            active: this.searchActive,
            visible: this.isFocused || this.searchActive,
          })}"
        >
          ${this.loading
            ? html`<div class="search--loader">
                <slot name="loader"></slot>
              </div>`
            : ''}
          <slot name="search-inactive"></slot>
          <div
            class=${classMap({
              'search-results-container': true,
              active: this.searchActive,
            })}
            aria-hidden="${!this.searchActive}"
            ?inert=${!this.searchActive}
          >
            <div class="search-results-inner">
              <slot name="search-results"></slot>
            </div>
          </div>
        </div>
      </div>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .search-container {
      background-color: var(--hh-container-background-color, #fff);
      overflow-y: auto;
      height: 100vh;
      display: none;
      height: 100%;
      max-height: calc(100% - ${unsafeCSS('var(--hh-header-height)')});
      overflow-x: auto;
      padding: 0;
    }

    .search-container.visible {
      display: block;
    }

    @media (min-width: 990px) {
      .search-container {
        height: fit-content;
        padding: 0 2.5vw;
        display: flex;
        max-height: none;
        margin: 40px 0;
      }

      .search-form-wrapper {
        display: flex;
      }
    }

    @media (min-width: 1200px) {
      .search-container {
        padding: 0 5.21vw;
      }
    }

    .search-results-container {
      display: none;
    }

    .search-results-container.active {
      display: block;
    }

    :host([no-results]) .search-container.active {
      overflow-y: hidden;
      touch-action: none;
    }

    @media (max-width: 989px) {
      .predictive-search-wrapper.pinned {
        position: fixed;
        background-color: var(--hh-container-background-color, #fff);
        top: 60px;
        height: calc(100lvh - 60px);
        width: 100%;
        z-index: 10;
        border-top: 1px solid var(--hh-color-light-grey-2, #e6e6e6);
      }
    }
  `
}

customElements.define('hh-predictive-search', HHPredictiveSearch)
